import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  Workflow,
  UserCog,
  Database,
  TrendingUp,
  Landmark,
  Cloud,
  ArrowRight,
} from 'lucide-react';
import ScrollReveal from '@/components/ScrollReveal';

gsap.registerPlugin(ScrollTrigger);

const services = [
  {
    icon: Workflow,
    title: 'AI Workflow Automation',
    description:
      'End-to-end automation of business workflows using AI components, orchestrated with controls and observability.',
    image: '/images/ai-workflow.jpg',
    href: '/capabilities?section=workflow-automation',
  },
  {
    icon: UserCog,
    title: 'Executive AI Assistants',
    description:
      'Secure assistants designed for executive contexts: briefing packs, KPIs, strategic synthesis, and decision support.',
    image: '/images/executive-ai.jpg',
    href: '/capabilities?section=executive-assistants',
  },
  {
    icon: Database,
    title: 'Enterprise RAG & Knowledge',
    description:
      'Enterprise-grade retrieval and knowledge systems that connect your internal content to assistants and workflows.',
    image: '/images/enterprise-rag.jpg',
    href: '/capabilities?section=rag-knowledge',
  },
  {
    icon: TrendingUp,
    title: 'AI Marketing Intelligence',
    description:
      'AI systems that support modern marketing teams with content generation, insights, analytics, and exec-ready reporting.',
    image: '/images/marketing-ai.jpg',
    href: '/capabilities?section=marketing-intelligence',
  },
  {
    icon: Landmark,
    title: 'FinTech & Regulatory AI',
    description:
      'AI for regulated financial services workflows — designed for auditability, traceability, and controlled operation.',
    image: '/images/fintech-ai.jpg',
    href: '/capabilities?section=fintech-regulatory',
  },
  {
    icon: Cloud,
    title: 'Cloud-Native Platforms',
    description:
      'The platform layer that makes AI work reliably at enterprise scale: deployment, security, CI/CD, and operational readiness.',
    image: '/images/cloud-platforms.jpg',
    href: '/capabilities?section=cloud-platforms',
  },
];

export default function Services() {
  const cardsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const cards = cardsRef.current?.querySelectorAll('.service-card');
      if (cards) {
        gsap.fromTo(
          cards,
          {
            opacity: 0,
            rotateY: -30,
            transformPerspective: 1000,
          },
          {
            opacity: 1,
            rotateY: 0,
            duration: 0.7,
            stagger: 0.1,
            ease: 'expo.out',
            scrollTrigger: {
              trigger: cardsRef.current,
              start: 'top 80%',
            },
          }
        );
      }
    }, cardsRef);

    return () => ctx.revert();
  }, []);

  return (
    <section className="section-padding bg-white">
      <div className="container-custom">
        {/* Section Header */}
        <ScrollReveal className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Capabilities
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Production-grade AI systems built for enterprise scale, security and
            compliance
          </p>
        </ScrollReveal>

        {/* Services Grid */}
        <div
          ref={cardsRef}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {services.map((service, index) => (
            <Link
              key={index}
              to={service.href}
              className="service-card group relative bg-white rounded-xl overflow-hidden border border-slate-200 shadow-card transition-all duration-500 hover:-translate-y-2 hover:shadow-card-hover"
              style={{ transformStyle: 'preserve-3d' }}
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-white via-white/50 to-transparent" />
                
                {/* Icon */}
                <div className="absolute bottom-4 left-6 w-12 h-12 bg-primary/10 backdrop-blur-sm rounded-lg flex items-center justify-center border border-primary/20 group-hover:bg-primary/20 transition-colors">
                  <service.icon className="w-6 h-6 text-primary" />
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-xl font-semibold text-slate-900 mb-3 group-hover:text-primary transition-colors">
                  {service.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-4">
                  {service.description}
                </p>
                <span className="inline-flex items-center gap-2 text-primary text-sm font-medium group-hover:gap-3 transition-all">
                  Learn more
                  <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
